import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { usePost } from "../../../hooks/usePost";
import { useToast } from "../../../store/ToastProvider";
import {
  Card,
  ColumnContainer,
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { ToggleOptionsButton } from "../../../components/buttons/ToggleOptionsButton";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  Icon,
  IconContainer,
  LegendCircle,
  StyledPlaceholder,
} from "../../../components/visuals/styledComponents";
import { graphPaletteV2 } from "../../../styles";
import { MilestoneProgressChart } from "./MilestoneProgressChart";
import { MilestoneProgressBars } from "./MilestoneProgressBars";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import { MILESTONE_TYPES, ENGAGEMENT_TYPES } from "../constants";
import { EngagementTable } from "./EngagementTable";
import {
  EngagementItem,
  EngagementRecord,
  StewardshipFilterParam,
} from "../../../models/stewardshipModels";
import { convertToEngagementItems } from "../utils";
import { Empty } from "antd";
import { MESSAGES } from "../../../messages";
import { ROUTES } from "../../../routes/routeNames";
import arrowIcon from "../../../assets/svg/arrow.svg";

const LegendContainer = styled.div<{ isBarChart?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 330px;
  width: 100%;
  justify-content: center;
  padding: 10px;
  padding-top: 20px;
  border-radius: 8px;
  overflow-y: auto;
  margin-top: 5px;
`;

const HoverIconContainer = styled(IconContainer)`
  transition: all 0.2s ease;
  &:hover {
    background: #ccf3df;
    transform: scale(1.08);
  }
`;

export const CompanyEngagement: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const navigate = useNavigate();
  const [isBarChart, setIsBarChart] = useState(true);
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [engagementData, setEngagementData] = useState<EngagementItem[]>([]);
  const { addToast } = useToast();

  const CHOOSE_CHART_OPTIONS = ["Manager engagement", "Milestone progress"];
  const shouldDisplayEngagementElements = engagementData.length > 0;

  const {
    data,
    sendData: getEngagementData,
    isLoading,
    error: getEngagementDataError,
  } = usePost<EngagementRecord[], StewardshipFilterParam>();

  useEffect(() => {
    getEngagementData("GET_ENGAGEMENT_DATA", {
      compass_cid: [companyId],
    });
  }, [companyId]);

  useEffect(() => {
    if (data) {
      setEngagementData(convertToEngagementItems(data));
    }
  }, [data]);

  useEffect(() => {
    if (getEngagementDataError) {
      addToast(
        MESSAGES.SCREENING_TOOL.ERROR_FETCHING_ENGAGEMENT_RECORDS,
        "error",
      );
    }
  }, [getEngagementDataError]);

  const handleOptionChange = (option: string): void => {
    setIsBarChart(option === CHOOSE_CHART_OPTIONS[0]);
  };

  const handleCardExpand = (isActive: boolean): void => {
    setIsCardExpanded(isActive);
  };
  return (
    <RowContainer ml={8} mt={1}>
      <Card w={100} h={"auto"} m="0 30px 0 10px" isLoading={isLoading}>
        <RowContainer mt={-1} jc="flex-start" ai="flex-start">
          <ColumnContainer ai="flex-start" w="75%">
            <RowContainer ai="center" jc="flex-start">
              <CompassParagraph fontSize={16} bold={true}>
                Engagement
              </CompassParagraph>
              <Tooltip title="View engagements">
                <HoverIconContainer
                  size={28}
                  bg="#e6f9ef"
                  br={30}
                  p={6}
                  m={0}
                  cursor="pointer"
                  onClick={(): void =>
                    navigate(`${ROUTES.COMPANY_ENGAGEMENTS}/${companyId}`)
                  }
                >
                  <Icon
                    src={arrowIcon}
                    alt="Go to engagements"
                    style={{ transform: "rotate(135deg)" }}
                  />
                </HoverIconContainer>
              </Tooltip>
            </RowContainer>
            <RowContainer jc="flex-end" mt={5} mb={1}>
              <ElementContainer mr={50}>
                {shouldDisplayEngagementElements && (
                  <ToggleOptionsButton
                    options={CHOOSE_CHART_OPTIONS}
                    handleOptionChange={handleOptionChange}
                  />
                )}
              </ElementContainer>
            </RowContainer>
            {shouldDisplayEngagementElements && (
              <RowContainer>
                {isBarChart ? (
                  <MilestoneProgressChart data={engagementData} />
                ) : (
                  <MilestoneProgressBars data={engagementData} />
                )}
              </RowContainer>
            )}
          </ColumnContainer>
          <ColumnContainer w="25%">
            {shouldDisplayEngagementElements && (
              <LegendContainer isBarChart={isBarChart}>
                {(isBarChart
                  ? ENGAGEMENT_TYPES.map((item) => item.displayValue)
                  : MILESTONE_TYPES.map((item) => item.displayValue)
                ).map((item, index) => (
                  <ElementContainer key={index} mb={5}>
                    <LegendCircle color={graphPaletteV2[index]}></LegendCircle>
                    <CompassParagraph color="#8B909A" fontSize={12}>
                      {item}
                    </CompassParagraph>
                  </ElementContainer>
                ))}
              </LegendContainer>
            )}
          </ColumnContainer>
        </RowContainer>
        {shouldDisplayEngagementElements && (
          <RowContainer mt={-15} mb={-1}>
            <ToggleAccordionBlueText handleChange={handleCardExpand} />
          </RowContainer>
        )}
        {isCardExpanded && <EngagementTable data={engagementData} />}
        {EmptyContentPlaceholders(shouldDisplayEngagementElements, isLoading)}
      </Card>
    </RowContainer>
  );
};

const EmptyContentPlaceholders = (
  shouldDisplayEngagementElements: boolean,
  isLoading: boolean,
): JSX.Element | null => {
  if (!shouldDisplayEngagementElements) {
    return isLoading ? (
      <FlexibleContainer fg={1} as="stretch" fd="column">
        <StyledPlaceholder xs={10} size="lg" bg="white" />
        <StyledPlaceholder xs={10} size="lg" bg="white" />
        <StyledPlaceholder xs={10} size="lg" bg="white" />
      </FlexibleContainer>
    ) : (
      <RowContainer jc="center">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={"No engagement records"}
        />
      </RowContainer>
    );
  }
  return null;
};
